import { Controller } from "@hotwired/stimulus"

// Resumable chunked upload of the ConfTool bulk PDF ZIP (200MB–2GB).
// Protocol lives in Admin::ConftoolImportZipUploadsController: create a session,
// PATCH sequential chunks with an Upload-Offset header, POST complete. The server's
// received_bytes is ALWAYS authoritative — any 409 carries it and the loop re-syncs
// and continues, which also covers replica races and crash-repaired part files.
//
// Uses XMLHttpRequest (not fetch): only XHR exposes upload.onprogress, needed for
// byte-accurate progress inside each 8 MiB chunk.
//
// Resilience: network errors / 5xx / 429 retry with exponential backoff + jitter and
// auto-resume (a `window online` event retries immediately); after 8 consecutive
// failures at the same offset the upload parks in a paused-error state with a manual
// Resume. A page reload keeps the server session — the user re-selects the same file
// (name + size verified) and the upload continues from the server offset.
export default class extends Controller {
  static values = {
    sessionUrl: String,
    chunkUrl: String,
    completeUrl: String,
    maxBytes: Number,
    chunkBytes: { type: Number, default: 8 * 1024 * 1024 },
    session: { type: Object, default: {} }
  }

  static targets = [
    "fileInput", "startButton", "idlePanel", "fileInfo",
    "resumeHint", "resumeHintText",
    "progressPanel", "progressBar", "statusLabel", "bytesLabel", "speedLabel", "etaLabel",
    "pauseButton", "errorPanel", "errorMessage", "resumeButton"
  ]

  static MAX_ATTEMPTS = 8

  connect() {
    this.file = null
    this.offset = 0
    this.uploading = false
    this.paused = false
    this.attempt = 0
    this.samples = []
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this)
    this.boundBeforeVisit = this.handleBeforeVisit.bind(this)
    this.boundOnline = this.handleOnline.bind(this)
    this.renderInitialState()
  }

  disconnect() {
    this.removeGuards()
    this.abortInFlight()
  }

  // --- entry points -----------------------------------------------------------------

  fileChanged() {
    const file = this.fileInputTarget.files[0]
    this.hideError()
    if (!file) { this.file = null; this.startButtonTarget.disabled = true; return }

    if (!/\.zip$/i.test(file.name)) {
      this.file = null
      this.startButtonTarget.disabled = true
      this.showError(`"${file.name}" is not a .zip archive.`)
      return
    }
    if (file.size > this.maxBytesValue) {
      this.file = null
      this.startButtonTarget.disabled = true
      this.showError(`The ZIP is larger than the ${this.formatBytes(this.maxBytesValue)} limit.`)
      return
    }

    this.file = file
    this.startButtonTarget.disabled = false
    this.fileInfoTarget.textContent = `${file.name} · ${this.formatBytes(file.size)}`
    this.fileInfoTarget.classList.remove("d-none")
  }

  async start() {
    if (!this.file || this.uploading) return
    this.hideError()

    const session = await this.ensureSession()
    if (!session) return // ensureSession already rendered the problem

    this.offset = session.received_bytes
    this.beginTransfer()
    await this.runUploadLoop()
  }

  async resumeFromError() {
    if (!this.file) return
    this.hideError()
    this.attempt = 0
    this.paused = false
    this.beginTransfer()
    await this.runUploadLoop()
  }

  togglePause() {
    if (this.paused) {
      this.paused = false
      this.pauseButtonTarget.textContent = "Pause"
      this.beginTransfer()
      this.runUploadLoop()
    } else {
      this.paused = true
      this.pauseButtonTarget.textContent = "Resume"
      this.setStatus("Paused — the upload continues where it stopped.")
      this.endTransfer()
      this.abortInFlight()
    }
  }

  async cancel() {
    if (!confirm("Cancel this upload and discard the bytes already received?")) return
    this.paused = true
    this.abortInFlight()
    this.endTransfer()
    await this.request("DELETE", this.sessionUrlValue)
    this.resetToIdle()
  }

  async discardSession() {
    await this.request("DELETE", this.sessionUrlValue)
    this.sessionValue = {}
    this.resumeHintTarget.classList.add("d-none")
    this.resetToIdle()
  }

  // --- session handling ---------------------------------------------------------------

  // Returns a session payload whose received_bytes we can start from, or null.
  async ensureSession() {
    const existing = await this.fetchCurrentSession()
    if (existing && existing.status === "uploading") {
      if (this.matchesFile(existing)) return existing

      const replace = confirm(
        `An interrupted upload of "${existing.filename}" exists, but the selected file is different. ` +
        "Replace it and discard the bytes already received?"
      )
      if (!replace) return null
      await this.request("DELETE", this.sessionUrlValue)
    }

    const { status, json } = await this.request("POST", this.sessionUrlValue, {
      body: JSON.stringify({ filename: this.file.name, byte_size: this.file.size }),
      contentType: "application/json"
    })

    if (status === 201) return json
    if (status === 409 && json?.session) {
      if (this.matchesFile(json.session) && json.session.status === "uploading") return json.session
      this.showError("Another upload for this import is already in progress. Reload the page to see it.")
      return null
    }
    if (status === 413) {
      this.showError(`The ZIP is larger than the ${this.formatBytes(json?.max_bytes || this.maxBytesValue)} limit.`)
      return null
    }
    if (status === 422) {
      this.showError((json?.messages || ["The file was rejected."]).join(", "))
      return null
    }
    this.showError("The upload could not be started. Please try again.")
    return null
  }

  async fetchCurrentSession() {
    const { status, json } = await this.request("GET", this.sessionUrlValue)
    return status === 200 ? json : null
  }

  matchesFile(session) {
    return session.filename === this.file.name && session.total_bytes === this.file.size
  }

  // --- the upload loop ----------------------------------------------------------------

  async runUploadLoop() {
    this.uploading = true
    try {
      while (this.offset < this.file.size && !this.paused) {
        const { status, json, retryAfter } = await this.sendChunk()

        if (status === 200) {
          this.offset = json.received_bytes
          this.attempt = 0
        } else if (status === 409) {
          // Authoritative offset from the server (stale client, replica race, repaired
          // part file) — adopt it and continue immediately.
          this.offset = json.received_bytes
          this.attempt = 0
        } else if (status === 410) {
          this.failFatal("This upload no longer exists on the server. Start over to upload the file.")
          return
        } else if (status === 413) {
          this.failFatal(json?.error === "chunk_too_large"
            ? "The upload chunk was rejected as too large."
            : "The server rejected the upload as larger than declared. Start over to retry.")
          return
        } else if (status === -1) {
          return // aborted by pause/cancel — state already handled
        } else if (this.looksSignedOut(status, json)) {
          this.parkWithResume("Your session expired. Sign in again in another tab, then press Resume.")
          return
        } else {
          // Network error (0), 5xx, 429, or a truncated body (400 short_body): back off
          // and retry from the same offset.
          this.attempt += 1
          if (this.attempt > this.constructor.MAX_ATTEMPTS) {
            this.parkWithResume("The upload was interrupted repeatedly. Check your connection, then press Resume.")
            return
          }
          await this.backoff(retryAfter)
        }
      }

      if (!this.paused) await this.complete()
    } finally {
      this.uploading = false
    }
  }

  sendChunk() {
    return new Promise((resolve) => {
      const end = Math.min(this.offset + this.chunkBytesValue, this.file.size)
      const chunk = this.file.slice(this.offset, end)
      const xhr = new XMLHttpRequest()
      this.xhr = xhr

      xhr.open("PATCH", this.chunkUrlValue)
      xhr.responseType = "json"
      xhr.setRequestHeader("Content-Type", "application/octet-stream")
      xhr.setRequestHeader("Upload-Offset", String(this.offset))
      xhr.setRequestHeader("Accept", "application/json")
      xhr.setRequestHeader("X-CSRF-Token", this.csrfToken())

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) this.renderProgress(this.offset + event.loaded)
      }
      xhr.onload = () => resolve({
        status: xhr.status, json: xhr.response,
        retryAfter: xhr.getResponseHeader("Retry-After")
      })
      xhr.onerror = () => resolve({ status: 0 })
      xhr.ontimeout = () => resolve({ status: 0 })
      xhr.onabort = () => resolve({ status: -1 })

      xhr.send(chunk)
    })
  }

  async complete() {
    this.setStatus("Finalizing — verifying and storing the archive…")
    const { status, json } = await this.request("POST", this.completeUrlValue)

    if (status === 200) {
      this.endTransfer()
      this.renderProgress(this.file.size)
      // The show page now renders the processing screen ("Storing uploaded ZIP" step).
      window.Turbo.visit(json.redirect_url)
    } else if (status === 409 && json?.received_bytes !== undefined) {
      this.offset = json.received_bytes
      await this.runUploadLoop() // a hole was repaired server-side — top it up
    } else if (status === 410) {
      this.failFatal("This upload no longer exists on the server. Start over to upload the file.")
    } else {
      this.parkWithResume("Finalizing failed. Press Resume to try again.")
    }
  }

  async backoff(retryAfter) {
    const base = Math.min(30_000, 1_000 * 2 ** this.attempt)
    const delay = retryAfter ? Number(retryAfter) * 1_000 : base + Math.random() * 1_000
    this.setStatus(`Connection lost — retrying in ${Math.round(delay / 1_000)}s… (attempt ${this.attempt}/${this.constructor.MAX_ATTEMPTS})`)
    await new Promise((resolve) => {
      this.retryTimer = setTimeout(resolve, delay)
      this.retryResolve = resolve
    })
    this.retryTimer = null
    this.retryResolve = null
    this.setStatus("Uploading…")
  }

  handleOnline() {
    // Back online: skip the rest of the backoff and retry immediately.
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryResolve?.()
    }
  }

  looksSignedOut(status, json) {
    // XHR follows the Devise redirect transparently; the login page then parses as
    // null JSON with a 200, and an explicit API rejection comes back as 401.
    return status === 401 || (status === 200 && json === null)
  }

  // --- UI state -----------------------------------------------------------------------

  renderInitialState() {
    const session = this.sessionValue
    if (session.status === "uploading" && session.received_bytes >= 0) {
      this.resumeHintTextTarget.textContent =
        `An interrupted upload of "${session.filename}" exists — ` +
        `${this.formatBytes(session.received_bytes)} of ${this.formatBytes(session.total_bytes)} received. ` +
        "Re-select the same file to continue where it left off, or discard it."
      this.resumeHintTarget.classList.remove("d-none")
    } else if (session.status === "failed" && session.error_message) {
      this.showError(session.error_message)
    }
  }

  beginTransfer() {
    this.addGuards()
    this.fileInputTarget.disabled = true
    this.startButtonTarget.disabled = true
    this.startButtonTarget.textContent = "Uploading…"
    this.resumeHintTarget.classList.add("d-none")
    this.progressPanelTarget.classList.remove("d-none")
    this.pauseButtonTarget.textContent = "Pause"
    this.setStatus("Uploading…")
  }

  endTransfer() {
    this.removeGuards()
  }

  resetToIdle() {
    this.file = null
    this.offset = 0
    this.attempt = 0
    this.paused = false
    this.fileInputTarget.disabled = false
    this.fileInputTarget.value = ""
    this.startButtonTarget.disabled = true
    this.startButtonTarget.textContent = "Upload ZIP & auto-match"
    this.fileInfoTarget.classList.add("d-none")
    this.progressPanelTarget.classList.add("d-none")
    this.hideError()
  }

  parkWithResume(message) {
    this.endTransfer()
    this.setStatus("Interrupted.")
    this.showError(message, { resumable: true })
  }

  failFatal(message) {
    this.endTransfer()
    this.progressPanelTarget.classList.add("d-none")
    this.fileInputTarget.disabled = false
    this.startButtonTarget.textContent = "Upload ZIP & auto-match"
    this.startButtonTarget.disabled = !this.file
    this.showError(message)
  }

  showError(message, { resumable = false } = {}) {
    this.errorMessageTarget.textContent = message
    this.errorPanelTarget.classList.remove("d-none")
    this.resumeButtonTarget.classList.toggle("d-none", !resumable)
  }

  hideError() {
    this.errorPanelTarget.classList.add("d-none")
    this.resumeButtonTarget.classList.add("d-none")
  }

  setStatus(text) {
    this.statusLabelTarget.textContent = text
  }

  renderProgress(uploadedBytes) {
    const total = this.file.size
    const bytes = Math.min(uploadedBytes, total)
    const percent = total === 0 ? 0 : Math.floor((bytes / total) * 100)

    this.progressBarTarget.style.width = `${percent}%`
    this.progressBarTarget.setAttribute("aria-valuenow", String(percent))
    this.bytesLabelTarget.textContent = `${this.formatBytes(bytes)} of ${this.formatBytes(total)} (${percent}%)`

    const now = performance.now()
    this.samples.push([now, bytes])
    while (this.samples.length > 2 && now - this.samples[0][0] > 5_000) this.samples.shift()
    const [t0, b0] = this.samples[0]
    const elapsed = (now - t0) / 1_000
    const rate = elapsed > 0.5 ? Math.max(0, (bytes - b0) / elapsed) : 0

    this.speedLabelTarget.textContent = rate > 0 ? `${this.formatBytes(rate)}/s` : "—"
    this.etaLabelTarget.textContent = rate > 0 ? `${this.formatDuration((total - bytes) / rate)} left` : ""
  }

  // --- guards -------------------------------------------------------------------------

  addGuards() {
    window.addEventListener("beforeunload", this.boundBeforeUnload)
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    window.addEventListener("online", this.boundOnline)
  }

  removeGuards() {
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    window.removeEventListener("online", this.boundOnline)
  }

  handleBeforeUnload(event) {
    event.preventDefault()
    event.returnValue = ""
  }

  handleBeforeVisit(event) {
    if (!confirm("An upload is in progress — leave anyway? You can resume it later from this page.")) {
      event.preventDefault()
    }
  }

  // --- helpers ------------------------------------------------------------------------

  abortInFlight() {
    if (this.xhr && this.xhr.readyState !== XMLHttpRequest.DONE) this.xhr.abort()
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryResolve?.()
    }
  }

  request(method, url, { body = null, contentType = null } = {}) {
    const headers = { "Accept": "application/json", "X-CSRF-Token": this.csrfToken() }
    if (contentType) headers["Content-Type"] = contentType
    return fetch(url, { method, headers, body, credentials: "same-origin" })
      .then(async (response) => {
        let json = null
        try { json = await response.json() } catch { /* 204 / non-JSON */ }
        return { status: response.status, json }
      })
      .catch(() => ({ status: 0, json: null }))
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  formatBytes(bytes) {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
    return `${bytes} B`
  }

  formatDuration(seconds) {
    const s = Math.max(0, Math.round(seconds))
    const m = Math.floor(s / 60)
    return m >= 1 ? `${m}:${String(s % 60).padStart(2, "0")} min` : `${s}s`
  }
}
